import { useMutation, useQuery } from "@tanstack/react-query"
import { useState } from "react"
import GameScreenLayout from "@/components/GameScreenLayout"
import { getDailyQuiz, verifyDailyQuiz } from "@/api/gameApi"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"

export default function AcademyQuizPage() {
  const { syncGameState } = useGuardiansPassport()
  const [answers, setAnswers] = useState<Record<string, string>>({})

  const quizQuery = useQuery({
    queryKey: ["daily-quiz"],
    queryFn: getDailyQuiz,
  })

  const submit = useMutation({
    mutationFn: () => {
      const questions = quizQuery.data?.questions ?? []
      return verifyDailyQuiz(questions, answers)
    },
    onSuccess: async () => {
      await syncGameState()
      void quizQuery.refetch()
    },
  })

  const questions = quizQuery.data?.questions ?? []
  const result = submit.data

  if (quizQuery.data?.cooldownUntil) {
    const until = new Date(quizQuery.data.cooldownUntil)
    if (until.getTime() > Date.now()) {
      return (
        <GameScreenLayout title="Академия" backTo="/play">
          <p className="text-sm text-guardians-muted">
            Квиз уже пройден. Следующий: {until.toLocaleString("ru-RU")}
          </p>
          {result ? (
            <p className="mt-3 text-sm">
              Результат: {result.correctCount}/{result.totalCount}
              {result.rewardGranted ? " · награда получена" : ""}
            </p>
          ) : null}
        </GameScreenLayout>
      )
    }
  }

  return (
    <GameScreenLayout title="Ежедневный квиз" backTo="/play">
      {quizQuery.isLoading ? (
        <p className="text-sm text-guardians-muted">Загрузка вопросов…</p>
      ) : questions.length === 0 ? (
        <p className="text-sm text-guardians-muted">Вопросов нет</p>
      ) : (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault()
            submit.mutate()
          }}
        >
          {questions.map((q, idx) => (
            <fieldset key={q.id} className="rounded-xl border border-white/10 p-3">
              <legend className="px-1 text-sm font-medium">
                {idx + 1}. {q.question}
              </legend>
              <ul className="mt-2 space-y-1">
                {q.options.map((opt) => (
                  <li key={opt}>
                    <label className="flex cursor-pointer items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name={q.id}
                        value={opt}
                        checked={answers[q.id] === opt}
                        onChange={() =>
                          setAnswers((prev) => ({ ...prev, [q.id]: opt }))
                        }
                      />
                      {opt}
                    </label>
                  </li>
                ))}
              </ul>
            </fieldset>
          ))}
          <button
            type="submit"
            disabled={
              submit.isPending ||
              questions.some((q) => !answers[q.id]?.trim())
            }
            className="w-full rounded-xl bg-guardians-accent py-3 text-sm font-medium text-guardians-bg disabled:opacity-40"
          >
            {submit.isPending ? "Проверка…" : "Отправить ответы"}
          </button>
        </form>
      )}

      {submit.isError ? (
        <p className="mt-3 text-sm text-red-300">
          {getGameApiErrorMessage(submit.error, "Не удалось проверить квиз")}
        </p>
      ) : null}

      {result ? (
        <div className="mt-4 rounded-xl border border-green-500/30 bg-green-500/10 p-3 text-sm">
          <p>
            Верно {result.correctCount} из {result.totalCount}
          </p>
          {result.rewardGranted ? (
            <p className="text-guardians-accent">Награда начислена</p>
          ) : null}
        </div>
      ) : null}
    </GameScreenLayout>
  )
}
